import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { AbstractControl, FormControl } from '@angular/forms';

interface SignatureValue {
  firstName: string;
  lastName: string;
  signatureDataUrl: string;
}

@Component({
  standalone: false,
  selector: 'wr-signature-field',
  templateUrl: './signature-field.component.html',
  styleUrls: ['./signature-field.component.scss'],
})
export class SignatureFieldComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() control!: AbstractControl;

  @ViewChild('signatureCanvas') canvasRef?: ElementRef<HTMLCanvasElement>;

  firstName = '';
  lastName = '';
  signatureDataUrl = '';

  private drawing = false;
  private lastX = 0;
  private lastY = 0;
  private ctx: CanvasRenderingContext2D | null = null;

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  get hasSignature(): boolean {
    return !!this.signatureDataUrl;
  }

  get isDisabled(): boolean {
    return this.control.disabled;
  }

  ngOnInit(): void {
    const raw = this.control.value as string | null;
    if (raw) {
      try {
        const parsed: SignatureValue = JSON.parse(raw);
        this.firstName = parsed.firstName ?? '';
        this.lastName = parsed.lastName ?? '';
        this.signatureDataUrl = parsed.signatureDataUrl ?? '';
      } catch { /* ignore malformed */ }
    }

    this.control.valueChanges.subscribe(val => {
      if (this.control.pristine && val) {
        try {
          const parsed: SignatureValue = JSON.parse(val);
          this.firstName = parsed.firstName ?? '';
          this.lastName = parsed.lastName ?? '';
          this.signatureDataUrl = parsed.signatureDataUrl ?? '';
          this.redrawFromDataUrl();
        } catch { /* ignore */ }
      }
    });
  }

  ngAfterViewInit(): void {
    this.initCanvas();
    if (this.signatureDataUrl) this.redrawFromDataUrl();
  }

  ngOnDestroy(): void {
    this.removeCanvasListeners();
  }

  private initCanvas(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    this.ctx = canvas.getContext('2d');
    if (!this.ctx) return;
    this.ctx.strokeStyle = '#1a1a1a';
    this.ctx.lineWidth = 2;
    this.ctx.lineCap = 'round';
    this.ctx.lineJoin = 'round';
    this.attachCanvasListeners(canvas);
  }

  private attachCanvasListeners(canvas: HTMLCanvasElement): void {
    canvas.addEventListener('mousedown', this.onMouseDown);
    canvas.addEventListener('mousemove', this.onMouseMove);
    canvas.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('mouseleave', this.onMouseUp);
    canvas.addEventListener('touchstart', this.onTouchStart, { passive: false });
    canvas.addEventListener('touchmove', this.onTouchMove, { passive: false });
    canvas.addEventListener('touchend', this.onMouseUp);
  }

  private removeCanvasListeners(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    canvas.removeEventListener('mousedown', this.onMouseDown);
    canvas.removeEventListener('mousemove', this.onMouseMove);
    canvas.removeEventListener('mouseup', this.onMouseUp);
    canvas.removeEventListener('mouseleave', this.onMouseUp);
    canvas.removeEventListener('touchstart', this.onTouchStart);
    canvas.removeEventListener('touchmove', this.onTouchMove);
    canvas.removeEventListener('touchend', this.onMouseUp);
  }

  private getPos(canvas: HTMLCanvasElement, clientX: number, clientY: number) {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }

  private readonly onMouseDown = (e: MouseEvent) => {
    if (this.isDisabled) return;
    this.drawing = true;
    const pos = this.getPos(e.target as HTMLCanvasElement, e.clientX, e.clientY);
    this.lastX = pos.x;
    this.lastY = pos.y;
  };

  private readonly onMouseMove = (e: MouseEvent) => {
    if (!this.drawing || this.isDisabled) return;
    const pos = this.getPos(e.target as HTMLCanvasElement, e.clientX, e.clientY);
    this.drawLine(this.lastX, this.lastY, pos.x, pos.y);
    this.lastX = pos.x;
    this.lastY = pos.y;
  };

  private readonly onMouseUp = () => {
    if (!this.drawing) return;
    this.drawing = false;
    this.onCanvasChanged();
  };

  private readonly onTouchStart = (e: TouchEvent) => {
    e.preventDefault();
    if (this.isDisabled) return;
    this.drawing = true;
    const touch = e.touches[0];
    const pos = this.getPos(e.target as HTMLCanvasElement, touch.clientX, touch.clientY);
    this.lastX = pos.x;
    this.lastY = pos.y;
  };

  private readonly onTouchMove = (e: TouchEvent) => {
    e.preventDefault();
    if (!this.drawing || this.isDisabled) return;
    const touch = e.touches[0];
    const pos = this.getPos(e.target as HTMLCanvasElement, touch.clientX, touch.clientY);
    this.drawLine(this.lastX, this.lastY, pos.x, pos.y);
    this.lastX = pos.x;
    this.lastY = pos.y;
  };

  private drawLine(x1: number, y1: number, x2: number, y2: number): void {
    if (!this.ctx) return;
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
  }

  private onCanvasChanged(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;
    this.signatureDataUrl = canvas.toDataURL('image/png');
    this.sync();
  }

  private redrawFromDataUrl(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.ctx || !this.signatureDataUrl) return;
    const img = new Image();
    img.onload = () => {
      this.ctx!.clearRect(0, 0, canvas.width, canvas.height);
      this.ctx!.drawImage(img, 0, 0);
    };
    img.src = this.signatureDataUrl;
  }

  onFirstNameChange(value: string): void {
    this.firstName = value;
    this.sync();
  }

  onLastNameChange(value: string): void {
    this.lastName = value;
    this.sync();
  }

  clearSignature(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas || !this.ctx) return;
    this.ctx.clearRect(0, 0, canvas.width, canvas.height);
    this.signatureDataUrl = '';
    this.sync();
  }

  private sync(): void {
    const value: SignatureValue = {
      firstName: this.firstName,
      lastName: this.lastName,
      signatureDataUrl: this.signatureDataUrl,
    };
    this.formControl.setValue(JSON.stringify(value));
    this.formControl.markAsDirty();
  }
}
